import React, { useEffect, type JSX } from 'react';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import { getPlaiceholder } from 'plaiceholder';
import getArticleSlugs from '../../../../helpers/getArticleSlugs';
import configureSentryScope from '../../../../helpers/configureSentryScope';
import getArticlePageViewModels from '../../../../helpers/getArticlePageViewModels';
import expandMarkdownSnippets from '../../../../helpers/expandMarkdownSnippets';
import buildComponentViewModelsFromEnhancedMarkdowns from '../../../../helpers/buildComponentViewModelsFromEnhancedMarkdowns';
import { type GameViewModel } from '@resources/js/typings/viewModels/gameViewModel';
import { type ArticleViewModel } from '@resources/js/typings/viewModels/article';
import useViewModels, {
  type ViewModels,
  ViewModelsProvider,
} from '../../../../contexts/useViewModels';
import Article from '../../../../components/Article/Article';
import { GameContextProvider } from '@resources/js/contexts/useGame';
import { SeriesOfArticlesProvider } from '../../../../contexts/useSeriesOfArticles';
import getGameSlugs from '../../../../helpers/getGameSlugs';
import getArticleCategories from '../../../../helpers/getArticleCategories';
import {
  type ArchonHeaderViewModel,
  getGameLabel,
} from '../../../../components/ArchonHeader/ArchonHeader';
import { type ArchonFooterViewModel } from '../../../../components/ArchonFooter/ArchonFooter';
import ArchonLayout from '../../../../layouts/ArchonLayout/ArchonLayout';
import { V3Theme } from '@resources/js/contexts/useTheme';
import { initRequest } from '../../../../helpers/rpgLogsApiRequestMetadata';
import config from '../../../../config';

export const ArticlePagePath =
  '/[gameSlug]/articles/[articleCategorySlug]/[articleSlug]';

function InnerArticlePage({
  viewModels,
  header,
  footer,
  article,
}: {
  viewModels: ViewModels;
  header: ArchonHeaderViewModel;
  footer: ArchonFooterViewModel;
  article: ArticleViewModel;
}): JSX.Element {
  const { setViewModels } = useViewModels();
  useEffect(() => {
    setViewModels(viewModels);
  }, [viewModels, setViewModels]);

  return (
    <ArchonLayout
      header={header}
      footer={footer}
      breadcrumbs={article.breadcrumbs}
      theme={{ ...V3Theme, headingStyle: 'v3-with-header-margin' }}
    >
      <Article {...article} gameLabel={getGameLabel(header)} />
    </ArchonLayout>
  );
}

export default function ArticlePage({
  game,
  header,
  footer,
  article,
  viewModels,
}: {
  game: GameViewModel;
  header: ArchonHeaderViewModel;
  footer: ArchonFooterViewModel;
  article: ArticleViewModel;
  viewModels: ViewModels;
}): JSX.Element {
  return (
    <ViewModelsProvider value={viewModels}>
      <GameContextProvider game={game}>
        <SeriesOfArticlesProvider>
          <InnerArticlePage
            viewModels={viewModels}
            header={header}
            footer={footer}
            article={article}
          />
        </SeriesOfArticlesProvider>
      </GameContextProvider>
    </ViewModelsProvider>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const gameSlugs = await getGameSlugs();
  const articleCategorySlugs = (await getArticleCategories()).map(
    (x) => x.slug,
  );

  const paths = [];
  for (const gameSlug of gameSlugs) {
    for (const articleCategorySlug of articleCategorySlugs) {
      const articleSlugs = await getArticleSlugs(articleCategorySlug, gameSlug);
      for (const articleSlug of articleSlugs) {
        if (config.isProduction || Math.random() < 0.05) {
          paths.push({
            params: {
              gameSlug,
              articleCategorySlug,
              articleSlug,
            },
          });
        }
      }
    }
  }

  if (config.isCI) {
    console.log(`Pre-building ${paths.length} articles`);
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  configureSentryScope(params);

  if (
    !params?.gameSlug ||
    !params?.articleSlug ||
    !params.articleCategorySlug ||
    Array.isArray(params.articleSlug) ||
    Array.isArray(params.articleCategorySlug) ||
    Array.isArray(params.gameSlug)
  ) {
    return {
      notFound: true,
    };
  }

  if (
    params.gameSlug === 'classic-wrath' ||
    params.gameSlug === 'classic-cata'
  ) {
    return {
      redirect: {
        statusCode: 307,
        destination: `/classic-mop/articles/${params.articleCategorySlug}/${params.articleSlug}`,
      },
    };
  }

  initRequest({
    route: '/[gameSlug]/articles/[articleCategorySlug]/[articleSlug]',
    path: `/${params.gameSlug}/articles/${params.articleCategorySlug}/${params.articleSlug}`,
  });

  const translationKeys = [
    'global.something_went_wrong_tile',
    'reports.loading_data',
  ];

  const pageViewModels = await getArticlePageViewModels(
    params.articleSlug,
    params.articleCategorySlug,
    translationKeys,
    params.gameSlug,
  );

  if (!pageViewModels.article) {
    // handle old bookmarks and search results
    if (params.gameSlug.length) {
      return {
        redirect: {
          statusCode: 307,
          destination: `/${params.gameSlug}/articles`,
        },
      };
    }

    return {
      notFound: true,
    };
  }

  pageViewModels.article.body = await expandMarkdownSnippets(
    pageViewModels.article.body,
  );
  pageViewModels.article.sidebar = await expandMarkdownSnippets(
    pageViewModels.article.sidebar,
  );

  const context = {
    pageViewModels,
    getPlaiceholder,
  };

  const componentViewModels = {
    ...(await buildComponentViewModelsFromEnhancedMarkdowns(
      [
        pageViewModels.article.body,
        '<TableOfContents maximumHeadingLevel={3} minimumNumberOfHeadings={3}>',
      ],
      context,
    )),
    ...(await buildComponentViewModelsFromEnhancedMarkdowns(
      [pageViewModels.article.sidebar],
      context,
    )),
  };

  return {
    props: {
      googleAnalytics: pageViewModels.googleAnalytics,
      adPlacements: pageViewModels.adPlacements,
      playwireAds: pageViewModels.playwireAds,
      translations: pageViewModels.translations,
      game: pageViewModels.game,
      header: pageViewModels.header,
      footer: pageViewModels.footer,
      article: pageViewModels.article,
      viewModels: componentViewModels,
    },
    revalidate: 600,
  };
};
